import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
	flexRender,
	getCoreRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2 } from "lucide-react";
import Button from "./Button";
import Select from "./Select";
import TagInput from "./TagInput";
import ClientUniverseAccessDialog from "./ClientUniverseAccessDialog";

const ADMIN_API = "/v1/admin";

function getHeaders() {
	const token = localStorage.getItem("token");
	return token ? { Authorization: `Bearer ${token}` } : {};
}

function ClientManagement({ onLogout }) {
	const [clients, setClients] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [sorting, setSorting] = useState([]);

	// Dialogs
	const [createOpen, setCreateOpen] = useState(false);
	const [editTarget, setEditTarget] = useState(null);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [secretData, setSecretData] = useState(null); // { clientId, secret }
	const [accessTarget, setAccessTarget] = useState(null); // clientId to manage universe access

	const fetchClients = useCallback(async () => {
		setIsLoading(true);
		try {
			const res = await fetch(`${ADMIN_API}/clients`, { headers: getHeaders() });
			if (res.ok) {
				setClients(await res.json());
			} else if (res.status === 401 && onLogout) {
				onLogout();
			} else {
				console.error("Failed to fetch clients", res.status, await res.text());
			}
		} catch (e) {
			console.error("Failed to fetch clients", e);
		} finally {
			setIsLoading(false);
		}
	}, [onLogout]);

	React.useEffect(() => { fetchClients(); }, [fetchClients]);

	const handleUpdated = useCallback(() => {
		setEditTarget(null);
		fetchClients();
	}, [fetchClients]);

	const handleDeleted = useCallback(() => {
		setDeleteTarget(null);
		fetchClients();
	}, [fetchClients]);

	const handleResetSecret = useCallback(async (clientId) => {
		try {
			const res = await fetch(`${ADMIN_API}/clients/${clientId}/reset-secret`, {
				method: "POST",
				headers: { ...getHeaders() },
			});
			if (res.ok) {
				const data = await res.json();
				setSecretData(data);
			} else {
				const msg = await res.text();
				alert(msg || "Errore durante il reset del secret");
			}
		} catch (e) {
			alert("Errore di connessione");
		}
	}, []);

	const columns = useMemo(() => [
		{
			accessorKey: "clientId",
			header: "Client ID",
			cell: ({ row }) => (
				<span className="font-mono text-xs font-medium text-slate-800">{row.getValue("clientId")}</span>
			),
		},
		{
			accessorKey: "description",
			header: "Description",
		},
		{
			accessorKey: "scopes",
			header: "Scopes",
			cell: ({ row }) => {
				const scopes = (row.getValue("scopes") || "").split(" ").filter(Boolean);
				return (
					<div className="flex flex-wrap gap-1">
						{scopes.map((s) => (
							<span key={s} className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${
								s.startsWith("v1.admin.") ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"
							}`}>
								{s}
							</span>
						))}
					</div>
				);
			},
		},
		{
			accessorKey: "expirationDate",
			header: "Expires",
			cell: ({ row }) => {
				const val = row.getValue("expirationDate");
				return <span className="text-sm text-slate-600">{val || "—"}</span>;
			},
		},
		{
			id: "actions",
			enableSorting: false,
			header: "",
			cell: ({ row }) => {
				const c = row.original;
				return (
					<div className="flex items-center gap-1">
						<button
							type="button"
							onClick={() => setAccessTarget(c.clientId)}
							className="inline-flex h-7 items-center justify-center rounded px-2 text-xs text-slate-600 hover:bg-slate-100"
							title="Manage Universe Access"
						>Universes</button>
						<button
							type="button"
							onClick={() => setEditTarget(c)}
							className="inline-flex h-7 w-7 items-center justify-center rounded text-xs text-slate-600 hover:bg-slate-100"
							title="Edit"
						>✎</button>
						<button
							type="button"
							onClick={() => handleResetSecret(c.clientId)}
							className="inline-flex h-7 w-7 items-center justify-center rounded text-xs text-slate-600 hover:bg-slate-100"
							title="Reset Secret"
						>🔑</button>
						<button
							type="button"
							onClick={() => setDeleteTarget(c)}
							className="inline-flex h-7 w-7 items-center justify-center rounded text-xs text-red-600 hover:bg-red-50"
							title="Delete"
						>✕</button>
					</div>
				);
			},
		},
	], [handleResetSecret]);

	const table = useReactTable({
		data: clients,
		columns,
		state: { sorting },
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
	});

	return (
		<section className="space-y-4">
			<div className="flex items-center justify-between">
				<h2 className="text-lg font-semibold text-slate-900">Client Management</h2>
				<Button onClick={() => setCreateOpen(true)} className="bg-blue-500 text-white">
					Create Client
				</Button>
			</div>

			<div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
				<div className="w-full overflow-x-auto">
					<table className="w-full caption-bottom text-sm">
						<thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
							{table.getHeaderGroups().map((hg) => (
								<tr key={hg.id} className="border-b border-slate-200">
									{hg.headers.map((h) => (
										<th key={h.id} className="h-10 px-4 align-middle">
											{h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
										</th>
									))}
								</tr>
							))}
						</thead>
						<tbody>
							{isLoading ? (
								<tr><td colSpan={5} className="px-4 py-10 text-center">
									<Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />
								</td></tr>
							) : table.getRowModel().rows.length ? (
								table.getRowModel().rows.map((row) => (
									<tr key={row.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
										{row.getVisibleCells().map((cell) => (
											<td key={cell.id} className="px-4 py-3 align-middle">
												{flexRender(cell.column.columnDef.cell, cell.getContext())}
											</td>
										))}
									</tr>
								))
							) : (
								<tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-500">
									Nessun client trovato.
								</td></tr>
							)}
						</tbody>
					</table>
				</div>
				<PaginationControls table={table} />
			</div>

			{createOpen && (
				<CreateClientDialog
					onClose={() => setCreateOpen(false)}
					onCreated={(data) => {
						setCreateOpen(false);
						setSecretData(data);
						fetchClients();
					}}
				/>
			)}

			{editTarget && (
				<EditClientDialog
					client={editTarget}
					onClose={() => setEditTarget(null)}
					onUpdated={handleUpdated}
				/>
			)}

			{deleteTarget && (
				<DeleteClientDialog
					client={deleteTarget}
					onClose={() => setDeleteTarget(null)}
					onDeleted={handleDeleted}
				/>
			)}

			{secretData && (
				<SecretDialog
					clientId={secretData.clientId}
					secret={secretData.secret}
					onClose={() => setSecretData(null)}
				/>
			)}
			{accessTarget && (
				<ClientUniverseAccessDialog
					open={!!accessTarget}
					onClose={() => setAccessTarget(null)}
					clientId={accessTarget}
				/>
			)}
		</section>
	);
}

ClientManagement.propTypes = { onLogout: PropTypes.func };

/* ────── Dialogs ────── */

function CreateClientDialog({ onClose, onCreated }) {
	const [clientId, setClientId] = useState("");
	const [description, setDescription] = useState("");
	const [scopes, setScopes] = useState([]);
	const [expirationDate, setExpirationDate] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState("");

	const handleSubmit = useCallback(async () => {
		if (!clientId.trim() || !description.trim()) {
			setError("clientId and description are required");
			return;
		}
		setIsSubmitting(true);
		setError("");
		try {
			const body = {
				clientId: clientId.trim(),
				description: description.trim(),
				scopes,
				expirationDate: expirationDate || null,
			};
			const res = await fetch(`${ADMIN_API}/clients`, {
				method: "POST",
				headers: { "Content-Type": "application/json", ...getHeaders() },
				body: JSON.stringify(body),
			});
			if (res.ok) {
				onCreated(await res.json());
			} else {
				setError(await res.text());
			}
		} catch { setError("Errore di connessione"); }
		finally { setIsSubmitting(false); }
	}, [clientId, description, scopes, expirationDate, onCreated]);

	React.useEffect(() => {
		return () => { setClientId(""); setDescription(""); setScopes([]); setExpirationDate(""); setError(""); };
	}, []);

	return (
		<DialogShell title="Create Client" onClose={onClose}>
			<div className="space-y-4 px-5 py-4">
				<Field label="Client ID *">
					<input type="text" value={clientId} onChange={(e) => setClientId(e.target.value)}
						className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500" />
				</Field>
				<Field label="Description *">
					<input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
						className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500" />
				</Field>
				<Field label="Scopes">
					<TagInput value={scopes} onChange={setScopes} placeholder="Digita scope e premi Enter..." />
				</Field>
				<Field label="Expiration Date (optional)">
					<input type="datetime-local" value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)}
						className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500" />
				</Field>
				{error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">{error}</div>}
			</div>
			<div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
				<Button variant="outline" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
				<Button onClick={handleSubmit} disabled={isSubmitting}>{isSubmitting ? "Creating..." : "Create"}</Button>
			</div>
		</DialogShell>
	);
}
CreateClientDialog.propTypes = { onClose: PropTypes.func.isRequired, onCreated: PropTypes.func.isRequired };

function EditClientDialog({ client, onClose, onUpdated }) {
	const [description, setDescription] = useState(client.description || "");
	const [scopes, setScopes] = useState((client.scopes || "").split(" ").filter(Boolean));
	const [expirationDate, setExpirationDate] = useState(client.expirationDate ? client.expirationDate.slice(0, 16) : "");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState("");

	const handleSubmit = useCallback(async () => {
		if (!description.trim()) { setError("description is required"); return; }
		setIsSubmitting(true);
		setError("");
		try {
			const body = { description: description.trim(), scopes, expirationDate: expirationDate || null };
			const res = await fetch(`${ADMIN_API}/clients/${client.clientId}`, {
				method: "PUT",
				headers: { "Content-Type": "application/json", ...getHeaders() },
				body: JSON.stringify(body),
			});
			if (res.ok) { onUpdated(); }
			else { setError(await res.text()); }
		} catch { setError("Errore di connessione"); }
		finally { setIsSubmitting(false); }
	}, [client.clientId, description, scopes, expirationDate, onUpdated]);

	return (
		<DialogShell title={`Edit Client: ${client.clientId}`} onClose={onClose}>
			<div className="space-y-4 px-5 py-4">
				<Field label="Description *">
					<input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
						className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500" />
				</Field>
				<Field label="Scopes">
					<TagInput value={scopes} onChange={setScopes} placeholder="Digita scope e premi Enter..." />
				</Field>
				<Field label="Expiration Date (optional)">
					<input type="datetime-local" value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)}
						className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500" />
				</Field>
				{error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">{error}</div>}
			</div>
			<div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
				<Button variant="outline" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
				<Button onClick={handleSubmit} disabled={isSubmitting}>{isSubmitting ? "Saving..." : "Save"}</Button>
			</div>
		</DialogShell>
	);
}
EditClientDialog.propTypes = { client: PropTypes.object.isRequired, onClose: PropTypes.func.isRequired, onUpdated: PropTypes.func.isRequired };

function DeleteClientDialog({ client, onClose, onDeleted }) {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState("");

	const handleConfirm = useCallback(async () => {
		setIsSubmitting(true);
		setError("");
		try {
			const res = await fetch(`${ADMIN_API}/clients/${client.clientId}`, {
				method: "DELETE",
				headers: { ...getHeaders() },
			});
			if (res.ok) { onDeleted(); }
			else { setError(await res.text()); }
		} catch { setError("Errore di connessione"); }
		finally { setIsSubmitting(false); }
	}, [client.clientId, onDeleted]);

	return (
		<DialogShell title="Delete Client" onClose={onClose}>
			<div className="px-5 py-4">
				<p className="text-sm text-slate-700">
					Are you sure you want to delete client <strong>{client.clientId}</strong>?
				</p>
				{error && <div className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-600">{error}</div>}
			</div>
			<div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
				<Button variant="outline" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
				<Button onClick={handleConfirm} disabled={isSubmitting} className="bg-red-500 text-white">
					{isSubmitting ? "Deleting..." : "Delete"}
				</Button>
			</div>
		</DialogShell>
	);
}
DeleteClientDialog.propTypes = { client: PropTypes.object.isRequired, onClose: PropTypes.func.isRequired, onDeleted: PropTypes.func.isRequired };

function SecretDialog({ clientId, secret, onClose }) {
	const [copied, setCopied] = useState(false);

	const handleCopy = () => {
		navigator.clipboard.writeText(secret).then(() => setCopied(true)).catch(() => {});
	};

	return (
		<DialogShell title="Client Secret" onClose={onClose}>
			<div className="space-y-3 px-5 py-4">
				<div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
					<strong>⚠ Copy this secret now.</strong> It will not be shown again.
				</div>
				<div>
					<p className="text-sm font-medium text-slate-700">Client ID</p>
					<p className="font-mono text-sm text-slate-900">{clientId}</p>
				</div>
				<div>
					<p className="text-sm font-medium text-slate-700">Secret</p>
					<div className="mt-1 flex items-center gap-2">
						<code className="flex-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-sm break-all">
							{secret}
						</code>
						<Button onClick={handleCopy} className="bg-blue-500 text-white whitespace-nowrap">
							{copied ? "Copied!" : "Copy"}
						</Button>
					</div>
				</div>
			</div>
			<div className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-3">
				<Button onClick={onClose}>Close</Button>
			</div>
		</DialogShell>
	);
}
SecretDialog.propTypes = { clientId: PropTypes.string.isRequired, secret: PropTypes.string.isRequired, onClose: PropTypes.func.isRequired };

/* ────── Shared UI ────── */

function DialogShell({ title, children, onClose }) {
	return (
		<dialog open className="fixed inset-0 z-[100] flex items-center justify-center w-screen h-screen m-0 max-w-none max-h-none border-0 bg-slate-900/60 backdrop-blur-sm backdrop-filter p-4" aria-modal="true" onCancel={onClose}>
			<div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
				<div className="border-b border-slate-200 px-5 py-4">
					<h2 className="text-lg font-semibold text-slate-900">{title}</h2>
				</div>
				{children}
			</div>
		</dialog>
	);
}
DialogShell.propTypes = { title: PropTypes.string.isRequired, children: PropTypes.node, onClose: PropTypes.func.isRequired };

function Field({ label, children }) {
	return (
		<div>
			<label className="block text-sm font-medium text-slate-700">{label}</label>
			{children}
		</div>
	);
}
Field.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node };

/* ────── Pagination ────── */

function PaginationControls({ table }) {
	return (
		<div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
			<div className="text-sm text-slate-600">
				{table.getFilteredRowModel().rows.length} righe totali
			</div>
			<div className="flex flex-wrap items-center gap-3">
				<div className="flex items-center gap-2 text-sm text-slate-700">
					<span>Righe per pagina</span>
					<Select
						ariaLabel="Righe per pagina"
						value={String(table.getState().pagination.pageSize)}
						onValueChange={(v) => table.setPageSize(Number(v))}
						options={[5, 10, 20, 30, 50].map((s) => ({ value: String(s), label: s }))}
						className="w-[120px]"
					/>
				</div>
				<div className="flex items-center gap-2 text-sm font-medium text-slate-800">
					<button onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}
						className="hidden h-8 w-8 items-center justify-center rounded border bg-white shadow-sm hover:bg-slate-100 disabled:opacity-50 sm:flex">
						<ChevronsLeft className="h-4 w-4" />
					</button>
					<button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}
						className="flex h-8 w-8 items-center justify-center rounded border bg-white shadow-sm hover:bg-slate-100 disabled:opacity-50">
						<ChevronLeft className="h-4 w-4" />
					</button>
					<span className="px-1 text-xs sm:text-sm">
						Pagina {table.getState().pagination.pageIndex + 1} di {table.getPageCount()}
					</span>
					<button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}
						className="flex h-8 w-8 items-center justify-center rounded border bg-white shadow-sm hover:bg-slate-100 disabled:opacity-50">
						<ChevronRight className="h-4 w-4" />
					</button>
					<button onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}
						className="hidden h-8 w-8 items-center justify-center rounded border bg-white shadow-sm hover:bg-slate-100 disabled:opacity-50 sm:flex">
						<ChevronsRight className="h-4 w-4" />
					</button>
				</div>
			</div>
		</div>
	);
}
PaginationControls.propTypes = { table: PropTypes.object.isRequired };

export default ClientManagement;
